/** Connection history belongs to the transport, including gaps before a reader mounts. */
export class ConnectionLifecycle {
  private connected = false
  private hasConnected = false
  private readonly statusListeners = new Set<(connected: boolean) => void>()
  private readonly reconnectListeners = new Set<() => void>()

  isConnected(): boolean {
    return this.connected
  }

  /** Native socket lifecycle handlers supply status; server payloads never do. */
  setConnected(connected: boolean): void {
    if (this.connected === connected) return
    const reconnecting = connected && this.hasConnected
    this.connected = connected
    this.hasConnected ||= connected
    this.deliver(this.statusListeners, connected)
    if (reconnecting) this.deliver(this.reconnectListeners, undefined)
  }

  /** Status readers receive a snapshot, then every transition. */
  subscribeToStatus(callback: (connected: boolean) => void): () => void {
    const listener = (connected: boolean) => callback(connected)
    this.statusListeners.add(listener)
    this.deliver(new Set([listener]), this.connected)
    return () => { this.statusListeners.delete(listener) }
  }

  /** Acquisitions are independent even when callers share the same callback. */
  subscribeToReconnect(callback: () => void): () => void {
    const listener = () => callback()
    this.reconnectListeners.add(listener)
    return () => { this.reconnectListeners.delete(listener) }
  }

  /** A deliberate teardown ends the session and releases its readers. */
  reset(): void {
    this.connected = false
    this.hasConnected = false
    this.statusListeners.clear()
    this.reconnectListeners.clear()
  }

  private deliver<T>(listeners: Set<(value: T) => void>, value: T): void {
    for (const listener of listeners) {
      try {
        listener(value)
      } catch (error) {
        // One reader must not prevent room recovery or another reader's resync.
        console.error('WebSocket lifecycle subscriber failed:', error)
      }
    }
  }
}
